/**
 * Papel de factura de la sucursal (bloque 11): tamaño de la hoja preimpresa de la imprenta, los márgenes en milímetros
 * donde el sistema imprime el cuerpo, cuántos renglones caben (tope de una factura) y la letra (matriz o láser).
 * «Guardar y probar» abre la hoja de prueba para calibrar contra el papel real. Todo queda en auditoría.
 */
import { PLANTILLA_FACTURA_DEFECTO, type EstructuraDTO, type FormatoPapel, type FuenteFactura } from '@mg/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';
import { AyudaVentana } from '@/ayuda/BotonAyuda.tsx';
import { Button } from '@/componentes/ui/button.tsx';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/componentes/ui/dialog.tsx';
import { Input, Select } from '@/componentes/ui/input.tsx';
import { Campo } from '@/componentes/ui/label.tsx';
import { mensajeDeError, patch } from '@/lib/api.ts';

type Sucursal = EstructuraDTO['sucursales'][number];
interface Formulario {
  formato: FormatoPapel;
  superior: string;
  izquierdo: string;
  derecho: string;
  inferior: string;
  renglones: string;
  fuente: FuenteFactura;
}

const aTexto = (n: number) => String(n).replace('.', ',');
/** Milímetros escritos por la dueña («45», «45,5») al medio milímetro; NaN si no es un número. */
const aMm = (v: string) => Math.round(Number(v.trim().replace(',', '.')) * 2) / 2;
const deFormato = (formato: FormatoPapel, p = PLANTILLA_FACTURA_DEFECTO[formato]): Formulario => ({ formato, superior: aTexto(p.superior_mm), izquierdo: aTexto(p.izquierdo_mm), derecho: aTexto(p.derecho_mm), inferior: aTexto(p.inferior_mm), renglones: String(p.renglones), fuente: p.fuente });

export function DialogoPapel({ sucursal, cerrar }: { sucursal: Sucursal | null; cerrar: () => void }) {
  return (
    <Dialog open={!!sucursal} onOpenChange={(o) => !o && cerrar()}>
      <DialogContent className="max-w-2xl" data-testid="dialogo-papel">{sucursal ? <FormularioPapel key={sucursal.id} sucursal={sucursal} cerrar={cerrar} /> : null}</DialogContent>
    </Dialog>
  );
}

/** El formulario nace con la plantilla de la sucursal (una instancia por sucursal: `key`). */
function FormularioPapel({ sucursal, cerrar }: { sucursal: Sucursal; cerrar: () => void }) {
  const qc = useQueryClient();
  const [f, setF] = useState<Formulario>(() => deFormato(sucursal.formato_impresion, sucursal.plantilla_factura));
  const set = <K extends keyof Formulario>(k: K, v: Formulario[K]) => setF((x) => ({ ...x, [k]: v }));
  const mm = { superior_mm: aMm(f.superior), izquierdo_mm: aMm(f.izquierdo), derecho_mm: aMm(f.derecho), inferior_mm: aMm(f.inferior) };
  const renglones = Number(f.renglones);
  const valido = Object.values(mm).every((n) => Number.isFinite(n) && n >= 0) && mm.superior_mm <= 150 && mm.inferior_mm <= 100 && mm.izquierdo_mm <= 40 && mm.derecho_mm <= 40 && Number.isInteger(renglones) && renglones >= 1 && renglones <= 60;
  const guardar = useMutation({
    mutationFn: (_probar: boolean) => patch<{ ok: boolean }>(`/consolidado/sucursales/${sucursal.id}`, { formato_impresion: f.formato, plantilla_factura: { ...mm, renglones, fuente: f.fuente } }),
    onSuccess: (_r, probar) => {
      toast.success('Papel de factura guardado', { description: 'Queda en auditoría.' });
      void qc.invalidateQueries({ queryKey: ['consolidado'] });
      void qc.invalidateQueries({ queryKey: ['sucursales'] });
      if (probar) window.open(`/imprimir/PRUEBA/${sucursal.id}`, '_blank');
      else cerrar();
    },
    onError: (e) => toast.error('No se pudo guardar', { description: mensajeDeError(e) }),
  });
  return (
    <>
        <DialogHeader>
          <div className="flex items-center gap-1"><DialogTitle>Papel de factura · {sucursal.nombre}</DialogTitle><AyudaVentana tema="configuracion.papel" /></div>
          <DialogDescription>La factura, la nota de crédito y la nota de débito salen sobre el papel de la imprenta. Aquí se indica dónde imprime el sistema; se afina con la hoja de prueba.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 md:grid-cols-2">
          <Campo etiqueta="Tamaño de la hoja" htmlFor="pf-formato" ayuda="Al cambiarlo vuelven las medidas de partida de ese papel">
            <Select id="pf-formato" value={f.formato} onChange={(e) => setF(deFormato(e.target.value as FormatoPapel))} data-testid="pf-formato">
              <option value="carta">Carta (8,5 × 11 in)</option>
              <option value="media_carta">Media carta horizontal (8,5 × 5,5 in)</option>
              <option value="media_carta_vertical">Media carta en hoja carta de pie (la mitad de arriba; se corta)</option>
            </Select>
          </Campo>
          <Campo etiqueta="Letra" htmlFor="pf-fuente">
            <Select id="pf-fuente" value={f.fuente} onChange={(e) => set('fuente', e.target.value as FuenteFactura)} data-testid="pf-fuente">
              <option value="matriz">Ancho fijo (matriz de punto Epson LX-300)</option>
              <option value="laser">Proporcional (láser HP)</option>
            </Select>
          </Campo>
          <p className="text-xs text-muted-foreground md:col-span-2" data-testid="pf-preimpreso">La factura solo es válida sobre el papel de la imprenta, que ya trae la cabecera y el número de control: el sistema no los imprime. Con más productos de los que caben, la caja pide dividir la venta.</p>
          <Campo etiqueta="Margen superior (mm)" htmlFor="pf-superior" ayuda="Del borde de arriba hasta «Cliente»: el alto de la cabecera de la imprenta">
            <Input id="pf-superior" inputMode="decimal" value={f.superior} onChange={(e) => set('superior', e.target.value)} data-testid="pf-superior" />
          </Campo>
          <Campo etiqueta="Margen inferior (mm)" htmlFor="pf-inferior" ayuda="Del borde de abajo hasta «Total Bs.»: el alto del pie de imprenta">
            <Input id="pf-inferior" inputMode="decimal" value={f.inferior} onChange={(e) => set('inferior', e.target.value)} data-testid="pf-inferior" />
          </Campo>
          <Campo etiqueta="Margen izquierdo (mm)" htmlFor="pf-izquierdo">
            <Input id="pf-izquierdo" inputMode="decimal" value={f.izquierdo} onChange={(e) => set('izquierdo', e.target.value)} data-testid="pf-izquierdo" />
          </Campo>
          <Campo etiqueta="Margen derecho (mm)" htmlFor="pf-derecho">
            <Input id="pf-derecho" inputMode="decimal" value={f.derecho} onChange={(e) => set('derecho', e.target.value)} data-testid="pf-derecho" />
          </Campo>
          <Campo etiqueta="Renglones que caben" htmlFor="pf-renglones" ayuda="La caja no emite una factura con más renglones">
            <Input id="pf-renglones" inputMode="numeric" value={f.renglones} onChange={(e) => set('renglones', e.target.value.replace(/\D/g, ''))} data-testid="pf-renglones" />
          </Campo>
        </div>
        {!valido ? <p className="text-xs text-mg-rojo" data-testid="pf-invalido">Revisa las medidas: milímetros de 0 a 150 arriba, 0 a 100 abajo, 0 a 40 a los lados; de 1 a 60 renglones.</p> : null}
        <DialogFooter>
          <Button variant="outline" onClick={cerrar}>Cancelar</Button>
          <Button variant="outline" disabled={!valido} cargando={guardar.isPending && guardar.variables === true} onClick={() => guardar.mutate(true)} data-testid="pf-probar">Guardar y probar</Button>
          <Button disabled={!valido} cargando={guardar.isPending && guardar.variables === false} onClick={() => guardar.mutate(false)} data-testid="pf-guardar">Guardar</Button>
        </DialogFooter>
    </>
  );
}
